import type { Order, OrderItem } from "~/interface/Order";
import _ from "lodash";

export default class OrderBuilder {

    private order: Order;

    constructor() {
        this.order = {
            uniqueid: "",
            uuid: "",
            alias: "",
            noprint: false,
            orderitems: []
        };
        return this;
    }

    withUniqueId(uniqueid: string) {
        this.order.uniqueid = uniqueid;
        return this;
    }

    withUuid(uuid: string) {
        this.order.uuid = uuid;
        return this;
    }

    withAlias(alias: string) {
        this.order.alias = alias;
        return this;
    }

    withNoPrint(noprint: boolean) {
        this.order.noprint = noprint;
        return this;
    }

    withOrderItems(items: any[]) {
        const _items : OrderItem[] = items.map( (line)=>{
            // Quantity floor — mirrors the mobile receive guard in AdapterMobileV1.
            // A qty-0 line is UNKILLABLE once it lands in the jobchain: PAY/VOID
            // build json2 as `quantity * -1` → `0`, and JobchainV1_1.voidItem only
            // decrements when `qtyToVoid < 0`, so the line survives every void and
            // every payment and pins the table open forever.
            // This line previously read `line.quantity || line.quantity || 0` — a
            // duplicated operand (every sibling here is `camelCase || lowercase`),
            // so a missing/NaN/empty quantity silently became 0 instead of falling
            // back to `qty`. expandItems emits both fields; read both, floor at 1.
            const _quantity = Number(line.quantity) > 0 ? Number(line.quantity)
                : Number(line.qty) > 0 ? Number(line.qty)
                : 1;
            return {
                uniqueid: line.uuid || line.uniqueid || "",
                subitemid: line.subItemId || line.subitemid || 0,
                name: line.name || "",
                price: line.price || 0,
                discountrequest: line.discountrequest || line.discountRequest || { mode: 0, value: 0 },
                quantity: _quantity,
                toppings: line.toppings || [],
                taglist: line.taglist || [],
                comments: line.comments || "",
                ...(line.isOpenMenu ? { isOpenMenu: true } : {}),
                ...(line.openMenuName ? { openMenuName: line.openMenuName } : {}),
                // "Force its own row." JobchainV1_1.genItemLineKey keys a oneLine
                // item by its uniqueid, so replay can never merge it with an
                // identical-looking line. Split lines from the cart carry it —
                // without it, splitting qty 4 into 3 + 1 collapsed straight back
                // into a single qty-4 row on the table.
                ...(line.oneLine ? { oneLine: true } : {}),
            }
        })
        this.order.orderitems = _items;
        return this;
    }

    withCustom(payload: Partial<Order>) {
        this.order = {
            ...this.order,
            ...payload
        };
        return this;
    }

    build(): Order {
        let order = _.cloneDeep(this.order);

        let _order = _.omitBy(order, (value) => {
            if (_.isString(value)) {
                return value.trim() === "";
            }
            if (_.isObject(value)) {
                return _.isEmpty(value);
            }
            return _.isUndefined(value);
        });

        return {
            ..._order,
        } as Order;
    }
}
